import { useState, useRef, useLayoutEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
    content: string
    shortcut?: string
    children: ReactNode
    side?: 'top' | 'bottom' | 'left' | 'right'
    className?: string
}

export function Tooltip({ content, shortcut, children, side = 'bottom', className = '' }: TooltipProps) {
    const [isVisible, setIsVisible] = useState(false)
    const [coords, setCoords] = useState({ top: 0, left: 0 })
    const triggerRef = useRef<HTMLDivElement>(null)
    const [tooltipNode, setTooltipNode] = useState<HTMLDivElement | null>(null)

    const updatePosition = () => {
        if (!triggerRef.current || !isVisible) return

        const rect = triggerRef.current.getBoundingClientRect()
        const tooltipRect = tooltipNode?.getBoundingClientRect() || { width: 0, height: 0 }

        const scrollY = window.scrollY
        const scrollX = window.scrollX
        const viewportWidth = document.documentElement.clientWidth

        let top = 0
        let left = 0

        // Calculate initial position based on side
        switch (side) {
            case 'bottom':
                top = rect.bottom + scrollY + 8
                left = rect.left + scrollX + rect.width / 2
                break
            case 'top':
                top = rect.top + scrollY - 8
                left = rect.left + scrollX + rect.width / 2
                break
            case 'right':
                top = rect.top + scrollY + rect.height / 2
                left = rect.right + scrollX + 8
                break
            case 'left':
                top = rect.top + scrollY + rect.height / 2
                left = rect.left + scrollX - 8
                break
        }

        // --- Boundary Checks ---
        // 1. Horizontal clamping
        if (side === 'top' || side === 'bottom') {
            const halfWidth = tooltipRect.width / 2
            const effectiveLeft = left - halfWidth
            const effectiveRight = left + halfWidth
            const padding = 12

            if (effectiveLeft < padding) {
                left = padding + halfWidth
            } else if (effectiveRight > viewportWidth - padding) {
                left = viewportWidth - padding - halfWidth
            }
        }

        setCoords({ top, left })
    }

    useLayoutEffect(() => {
        if (isVisible) {
            updatePosition()
            requestAnimationFrame(() => updatePosition())

            const resizeObserver = new ResizeObserver(() => updatePosition())

            if (triggerRef.current) resizeObserver.observe(triggerRef.current)
            if (tooltipNode) resizeObserver.observe(tooltipNode)

            window.addEventListener('resize', updatePosition)
            window.addEventListener('scroll', updatePosition)

            return () => {
                resizeObserver.disconnect()
                window.removeEventListener('resize', updatePosition)
                window.removeEventListener('scroll', updatePosition)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updatePosition reads refs/props that are stable per render
    }, [isVisible, tooltipNode])

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={() => {
                    updatePosition()
                    setIsVisible(true)
                }}
                onMouseLeave={() => setIsVisible(false)}
                className={className}
            >
                {children}
            </div>

            {isVisible &&
                createPortal(
                    <div
                        ref={setTooltipNode}
                        className="fixed z-[9999] pointer-events-none flex flex-col items-center"
                        style={{
                            top: coords.top,
                            left: coords.left,
                            transform:
                                side === 'bottom' ? 'translate(-50%, 0)' :
                                    side === 'top' ? 'translate(-50%, -100%)' :
                                        side === 'right' ? 'translate(0, -50%)' :
                                            'translate(-100%, -50%)',
                        }}
                    >
                        <div
                            className="px-2.5 py-1.5 rounded-lg shadow-xl text-xs font-medium flex items-center gap-2 border"
                            style={{
                                backgroundColor: 'hsl(var(--color-bg-elevated))',
                                color: 'hsl(var(--color-text-primary))',
                                borderColor: 'hsl(var(--color-border))',
                            }}
                        >
                            <span>{content}</span>
                            {shortcut && (
                                <kbd
                                    className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-sans"
                                    style={{
                                        backgroundColor: 'hsl(var(--color-bg-secondary))',
                                        color: 'hsl(var(--color-text-secondary))',
                                    }}
                                >
                                    {shortcut}
                                </kbd>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    )
}
